// 🤖 API Craft Back Office - AI Chat Hook
import { useState, useEffect, useCallback } from 'react';
import { ChatMessage, AiResponse } from '../types';
import { aiApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

export const useAiChat = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate user-specific storage key
  const getChatStorageKey = useCallback(() => {
    if (!user?.id) {
      return 'apicraft_ai_chat_history_anonymous';
    }
    return `apicraft_ai_chat_history_${user.id}`;
  }, [user?.id]);

  // Migrate old global chat history to user-specific storage (one-time migration)
  const migrateOldChatHistory = useCallback(() => {
    if (!user?.id) return;

    const oldKey = 'apicraft_ai_chat_history';
    const newKey = getChatStorageKey();
    
    // Check if old data exists and new data doesn't
    const oldData = localStorage.getItem(oldKey);
    const newData = localStorage.getItem(newKey);
    
    if (oldData && !newData) {
      try {
        // Copy old data to new key
        localStorage.setItem(newKey, oldData);
        // Remove old data
        localStorage.removeItem(oldKey);
      } catch (error) {

      }
    }
  }, [user?.id, getChatStorageKey]);

  // Load chat history from localStorage on mount or when user changes
  useEffect(() => {
    // Don't do anything while auth is still loading
    if (authLoading) {
      return;
    }
    
    if (!user?.id) {
      setMessages([]); // Clear messages if no user
      return;
    }

    // First, try to migrate old data
    migrateOldChatHistory();

    try {
      const storageKey = getChatStorageKey();
      const savedChat = localStorage.getItem(storageKey);
      
      if (savedChat) {
        const parsedMessages = JSON.parse(savedChat);
        // Convert timestamp strings back to Date objects
        const messagesWithDates = parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        // Success: messages loaded
        setMessages(messagesWithDates);
      } else {
        setMessages([]); // No saved history for this user
      }
    } catch (error) {

      // Clear corrupted data
      const storageKey = getChatStorageKey();
      localStorage.removeItem(storageKey);
      setMessages([]);
    }
  }, [authLoading, user?.id, getChatStorageKey, migrateOldChatHistory]);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0 && user?.id) {
      try {
        const storageKey = getChatStorageKey();
        localStorage.setItem(storageKey, JSON.stringify(messages));
      } catch (error) {

      }
    }
  }, [messages, user?.id, getChatStorageKey]);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;
    
    if (!user?.id) {
      setError('Please log in to use the AI assistant');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Add user message
    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    addMessage(userMessage);

    try {
      // Call AI API
      const response: AiResponse = await aiApi.chat(content.trim());

      // Add AI response
      const aiMessage: ChatMessage = {
        id: `ai_${Date.now()}`,
        type: 'assistant',
        content: response.ai_response,
        timestamp: new Date(),
        tokens_used: response.tokens_used,
        execution_time_ms: response.execution_time_ms,
        generated_sql: response.generated_sql,
        query_results: response.query_results
      };

      addMessage(aiMessage);

    } catch (error: any) {
      setError(error.message || 'Error communicating with AI assistant');
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        type: 'assistant',
        content: 'Sorry, there was an error processing your query. Please try again.',
        timestamp: new Date()
      };

      addMessage(errorMessage);

    } finally {
      setIsLoading(false);
    }
  }, [addMessage, user?.id]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    if (user?.id) {
      const storageKey = getChatStorageKey();
      localStorage.removeItem(storageKey);
    }
  }, [user?.id, getChatStorageKey]);

  const exportChat = useCallback(() => {
    const chatData = {
      exported_at: new Date().toISOString(),
      user_id: user?.id || 'anonymous',
      user_email: user?.email || 'unknown',
      total_messages: messages.length,
      messages: messages
    };

    const blob = new Blob([JSON.stringify(chatData, null, 2)], {
      type: 'application/json'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const userPrefix = user?.email ? `${user.email.split('@')[0]}_` : '';
    a.download = `ai_chat_export_${userPrefix}${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [messages, user?.id, user?.email]);

  // Development helper: Clear all AI chat history for all users
  const clearAllChats = useCallback(() => {
    // Get all localStorage keys that match our pattern
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('apicraft_ai_chat_history_')) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all matching keys
    keysToRemove.forEach(key => localStorage.removeItem(key));
    setMessages([]);
    setError(null);
  }, []);



  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat,
    clearAllChats, // For development purposes
    exportChat,
    hasMessages: messages.length > 0
  };
};
